import React from 'react';
import { Share, Bookmark, Plus, SquarePlus, Search, ChevronLeft, RotateCw, X } from 'lucide-react';

// iOS Safari「添加到主屏幕」教程（我的页 / 管理员我的页共用）。
// 步骤和 iOS 上的真实操作一致：右下角「···」→「共享」→「添加到主屏幕」→ 右上角「添加」。
// 每一步下面画一个和真实界面一致的小示意（同样的图标、同样的文字），被点的那一项用红圈标出来，
// 其余选项调淡，一眼就能对上。不放真实截图（截图里可能有个人信息，也会随系统版本过时）。

const RED = '#FF3B30';

const ring = {
  outline: `2px solid ${RED}`,
  outlineOffset: 2,
  borderRadius: 12,
};

const mock = {
  background: 'var(--c-bg)',
  borderRadius: 12,
  padding: '10px 12px',
  marginTop: 8,
  overflow: 'hidden',
};

const dim = { opacity: 0.4 };

// 三个点
function Dots({ color = 'var(--c-text)' }) {
  return (
    <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
      {[0, 1, 2].map(i => <span key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: color }} />)}
    </span>
  );
}

// 圆形工具栏按钮（Safari 底栏的返回 / 更多）
function BarBtn({ children, hl }) {
  return (
    <span style={{
      width: 34, height: 34, borderRadius: '50%', background: 'var(--c-surface, #fff)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      ...(hl ? { outline: `2px solid ${RED}`, outlineOffset: 2 } : null),
    }}>{children}</span>
  );
}

// 菜单里的一行：图标 + 文字
function MenuRow({ icon, label, hl, faded }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
      background: 'var(--c-surface, #fff)', borderRadius: 10, marginBottom: 6,
      fontSize: 14, color: 'var(--c-text)',
      ...(faded ? dim : null),
      ...(hl ? ring : null),
    }}>
      {icon}
      <span style={{ fontWeight: hl ? 700 : 400 }}>{label}</span>
    </div>
  );
}

const STEPS = [
  {
    title: <>点 Safari 右下角的 <strong>「···」</strong>（三个点）</>,
    visual: (
      <div style={{ ...mock, display: 'flex', alignItems: 'center', gap: 8 }}>
        <BarBtn><ChevronLeft size={16} color="var(--c-text)" /></BarBtn>
        <span style={{
          flex: 1, height: 34, borderRadius: 17, background: 'var(--c-surface, #fff)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 12px', fontSize: 13, color: 'var(--c-text)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}>
          <span style={{ flex: 1, textAlign: 'center' }}>{typeof window !== 'undefined' ? window.location.hostname : ''}</span>
          <RotateCw size={14} color="var(--c-text)" />
        </span>
        <BarBtn hl><Dots /></BarBtn>
      </div>
    ),
  },
  {
    title: <>在弹出的菜单里点第一项 <strong>「共享」</strong></>,
    visual: (
      <div style={mock}>
        <MenuRow hl icon={<Share size={18} color="var(--c-text)" />} label="共享" />
        <MenuRow faded icon={<Bookmark size={18} color="var(--c-text)" />} label="添加到书签" />
        <MenuRow faded icon={<Plus size={18} color="var(--c-text)" />} label="新建标签页" />
      </div>
    ),
  },
  {
    title: <>在分享面板里<strong>向上滑动</strong>，找到并点 <strong>「添加到主屏幕」</strong></>,
    visual: (
      <div style={mock}>
        <MenuRow faded icon={<Search size={18} color="var(--c-text)" />} label="在页面上查找" />
        <MenuRow hl icon={<SquarePlus size={18} color="var(--c-text)" />} label="添加到主屏幕" />
      </div>
    ),
  },
  {
    title: <>右上角点蓝色的 <strong>「添加」</strong>，桌面就出现「React」图标</>,
    visual: (
      <div style={mock}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <BarBtn><X size={16} color="var(--c-text)" /></BarBtn>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--c-text)' }}>添加到主屏幕</span>
          <span style={{
            background: '#0A84FF', color: '#fff', fontSize: 14, fontWeight: 600,
            padding: '6px 16px', ...ring, borderRadius: 17,
          }}>添加</span>
        </div>
        <div style={{
          marginTop: 10, padding: '8px 10px', background: 'var(--c-surface, #fff)', borderRadius: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, color: 'var(--c-text)',
        }}>
          <span>作为网页 App 打开</span>
          <span style={{
            width: 40, height: 24, borderRadius: 12, background: '#34C759', position: 'relative', flexShrink: 0,
          }}>
            <span style={{ position: 'absolute', right: 2, top: 2, width: 20, height: 20, borderRadius: '50%', background: '#fff' }} />
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 6 }}>
          「作为网页 App 打开」保持开启（绿色），体验和 App 一致
        </div>
      </div>
    ),
  },
];

export default function IOSInstallGuide() {
  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 14, lineHeight: 1.6 }}>
        iPhone / iPad 请按以下 4 步安装，安装后可从桌面直接打开，体验与 App 一致。
      </div>

      {STEPS.map((st, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 26, height: 26, borderRadius: '50%', flexShrink: 0, marginTop: 1,
            background: 'var(--c-primary)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700,
          }}>{i + 1}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, color: 'var(--c-text)', lineHeight: 1.6 }}>{st.title}</div>
            {st.visual}
          </div>
        </div>
      ))}

      <div style={{
        fontSize: 12, color: 'var(--c-text-secondary)',
        background: 'var(--c-bg)', borderRadius: 10, padding: '10px 12px', lineHeight: 1.7,
      }}>
        <div>⚠️ 必须用 Safari 浏览器打开才能添加。微信内打开请点右上角「···」→「在 Safari 中打开」。</div>
        <div style={{ marginTop: 4 }}>
          没看到「···」？较旧的系统直接点 Safari 底部的 <strong>分享</strong> 按钮（方框加向上箭头，iPad 在顶部栏右侧），然后从第 3 步继续。
        </div>
      </div>
    </div>
  );
}
